import React from "react"
import cx from "classnames"
import { useStaticQuery, graphql } from "gatsby"
import styled from "@emotion/styled"
import Button, { DropdownButton } from "src/components/Button"
import TextLink from "src/components/TextLink"
import ScrollEntrance from "src/components/ScrollEntrance"
import SanityRichText from "src/components/SanityRichText"
import { typography, mq } from "src/styles"
import { themes } from "src/styles/themes"
import { getSlugLink } from "src/utils/format"

const Wrapper = styled.div`
  display: inline-block;
  display: block;
  vertical-align: top;
  text-align: ${({ alignment }) => alignment};
  ${({ alignment }) =>
    alignment === "center" &&
    `
		margin-left: auto;
		margin-right: auto;
		> div,
		.embeded-content {
			margin-left: auto;
			margin-right: auto;
		}
	`}
  ${mq.mediumAndBelow} {
    display: block;
  }
  dl {
    margin: 1.5em 0;
    li:not(:first-of-type) {
      margin-top: 0.75em;
    }
    dt {
      font-weight: ${typography.bold};
    }
  }
`
const TextContainer = styled.div`
  width: 100%;
  ${({ alignment }) =>
    alignment === "center" &&
    `
		margin-left: auto;
		margin-right: auto;
		h1, h2, h3, h4, h5, h6, p, blockquote, ul {
			margin-left: auto;
			margin-right: auto;
		}
	`}
  ${({ alignment }) =>
    alignment === "right" &&
    `
		margin-left: auto;
		h1, h2, h3, h4, h5, h6, p, blockquote, ul {
			margin-left: auto;
		}
	`}
`

const Eyebrow = styled.p`
  ${typography.eyebrow}
  color: var(--main-color) !important;
  ${({ hasText }) =>
    hasText
      ? `
		margin: 0 0 0.5em;
	`
      : `
		margin: 0;
	`}
`

const Headline = styled.h3`
	${({ headlineSize }) => typography[headlineSize]}
	${({ hasText }) =>
    !hasText &&
    `
		margin-bottom: 0;
	`}
	${({ hasEyebrow }) =>
    !hasEyebrow &&
    `
		margin-top: 0;
	`}
`

const Text = styled.div`
  ${({ newStyle }) =>
    !newStyle &&
    `
      > div {
        > p:not(.large):not(.medium):not(.small):not(.tiny) {
          ${({ textSize }) => (textSize ? typography[textSize] : "")}
        }
      }
  `}
  p.large {
    ${typography.bodyLarge};
  }
  p.medium {
    ${typography.bodyMedium};
  }
  p.small {
    ${typography.bodySmall};
  }
  p {
    ${typography.bodyNew};
  }
`

const ActionWrapper = styled.div`
  padding: 16px 10px 0;
  display: inline-block;
  vertical-align: middle;
`

const ButtonActions = styled.div`
  margin-top: 8px;
  text-align: ${({ alignment }) => alignment};
  margin-left: -10px;
  margin-right: -10px;
`

const TextLockup = ({
  theme = "default",
  eyebrow,
  eyebrowStyle,
  headline,
  headingSize,
  headlineSize,
  headlineElement,
  text,
  newStyle,
  textSize,
  textStyle,
  actions,
  className,
  alignment,
  entranceDelay,
  transitionIn = true,
  listType,
}) => {
  const { sanitySiteSettings } = useStaticQuery(
    graphql`
      query {
        sanitySiteSettings {
          title
          bannerText
          bannerColor
          ctaLinks {
            customerLoansLogin {
              ...Link
            }
            customerLeasesLogin {
              ...Link
            }
            businessSignUp {
              ...Link
            }
            businessLogin {
              ...Link
            }
          }
        }
      }
    `
  )

  const siteSettings = sanitySiteSettings
  const { ctaLinks } = siteSettings

  const { customerLoansLogin, customerLeasesLogin } = ctaLinks

  if (!text && !actions && !headline) {
    return false
  }
  eyebrow = eyebrow || text?.eyebrow

  text = text?._rawText || text?.text || text

  return (
    <Wrapper
      className={cx(className, newStyle ? "text-new" : "")}
      alignment={alignment}
    >
      <div>
        <TextContainer
          alignment={alignment}
          delay={entranceDelay}
          transitionIn={transitionIn}
          as={transitionIn ? ScrollEntrance : "div"}
        >
          {eyebrow && (
            <div>
              <Eyebrow
                className="eyebrow"
                hasText={headline || text}
                eyebrowStyle={eyebrowStyle}
                alignment={alignment}
                newStyle={newStyle}
              >
                {eyebrow}
              </Eyebrow>
            </div>
          )}

          {headline && (
            <div>
              <Headline
                as={headlineElement || headlineSize}
                headlineSize={headingSize ? headingSize : headlineSize}
                hasText={text}
                hasEyebrow={eyebrow}
                newStyle={newStyle}
              >
                {headline}
              </Headline>
            </div>
          )}

          {text && Array.isArray(text) && (
            <Text
              className="text"
              textSize={textSize}
              textStyle={textStyle}
              alignment={alignment}
              newStyle={newStyle}
            >
              <SanityRichText
                text={text}
                listType={listType}
                newStyle={newStyle}
              />
            </Text>
          )}

          {typeof text === "string" && (
            <Text
              className="text"
              textSize={textSize}
              textStyle={textStyle}
              alignment={alignment}
              newStyle={newStyle}
            >
              <p>{text}</p>
            </Text>
          )}

          {text && typeof text !== "string" && !Array.isArray(text) && (
            <Text
              className="text"
              textSize={textSize}
              textStyle={textStyle}
              alignment={alignment}
              newStyle={newStyle}
            >
              {text}
            </Text>
          )}

          {actions && actions.length > 0 && (
            <ButtonActions
              buttons={actions}
              alignment={alignment}
              className="actions"
            >
              {actions.map((action, index) => {
                const actionType = action.type || "pageLink"
                const actionTo =
                  actionType === "externalLink"
                    ? action.externalLink
                    : getSlugLink(action.link, false, action.linkSection)
                const actionToAccessible =
                  actionType === "externalLink"
                    ? action.externalLink
                    : action?.link?.content?.main?.title
                if (action._type === "newsletterSignup") {
                  return <div>NEWSLETTER SIGNUP (Service?)</div>
                } else if (action._type === "button") {
                  let actionTheme = "default"
                  if (action.theme === "primary") {
                    actionTheme = themes[theme]?.buttonTheme || "default"
                  } else if (action.theme === "secondary") {
                    actionTheme =
                      themes[theme]?.buttonThemeSecondary || "default"
                  }
                  if (actionType === "customerLogin") {
                    return (
                      <ActionWrapper key={"button-" + index}>
                        <DropdownButton
                          setTheme={actionTheme}
                          title={action.title}
                          name={action.title}
                          options={[
                            {
                              title: "Loans Customer",
                              to: customerLoansLogin,
                            },
                            {
                              title: "Leases Customer",
                              to: customerLeasesLogin,
                            },
                          ]}
                        >
                          {action.title}
                        </DropdownButton>
                      </ActionWrapper>
                    )
                  } else {
                    return (
                      <ActionWrapper key={"button-" + index}>
                        <Button
                          setTheme={actionTheme}
                          to={actionTo}
                          external={
                            actionType === "externalLink" || action.newTab
                          }
                          target={action.newTab ? "_blank" : ""}
                          title={
                            action?.title?.toLowerCase() === "learn more"
                              ? "Go to " + actionToAccessible
                              : action.title
                          }
                          name={
                            action?.title?.toLowerCase() === "learn more"
                              ? "Go to " + actionToAccessible
                              : action.title
                          }
                        >
                          {action.title}
                        </Button>
                      </ActionWrapper>
                    )
                  }
                } else {
                  return (
                    <ActionWrapper key={"button-" + index}>
                      <TextLink
                        to={actionTo}
                        external={
                          actionType === "externalLink" || action.newTab
                        }
                        target={action.newTab ? "_blank" : ""}
                        title={
                          action?.title?.toLowerCase() === "learn more"
                            ? "Go to " + actionToAccessible
                            : action.title
                        }
                        name={
                          action?.title?.toLowerCase() === "learn more"
                            ? "Go to " + actionToAccessible
                            : action.title
                        }
                      >
                        {action.title}
                      </TextLink>
                    </ActionWrapper>
                  )
                }
              })}
            </ButtonActions>
          )}
        </TextContainer>
      </div>
    </Wrapper>
  )
}

TextLockup.defaultProps = {
  alignment: "inherit",
  textSize: "body",
  entranceDelay: 0,
  transitionIn: true,
  headlineSize: "h3",
}

export default TextLockup
